import type { QueryClient } from '@tanstack/react-query';
import { FormattedMessage, type IntlShape } from 'react-intl';

import type {
  AssetSummaryResponse,
  ExtractionJobResponse,
  ExtractionJobStatus,
  PropertyListingResponse,
} from '@/client/types.gen';
import { assetKeys, enrichAsset } from '@/lib/asset-api';
import { extractionJobKeys, retryExtractionJob } from '@/lib/extraction-jobs-api';
import { enrichListing, listingKeys } from '@/lib/listing-api';
import { route } from '@/routes/paths';
import { ProcessingErrorMessage } from './ProcessingErrorMessage';
import { classificationLabel, jobLabel, jobTargetRoute } from './job-presentation';
import type { ProcessingAction, ProcessingState } from './processing-model';
import type { ProcessingCandidate } from './useTrackedProcessingItems';

type Retry = (key: string, operation: () => Promise<unknown>) => Promise<void>;

interface AdapterContext {
  intl: IntlShape;
  scopeId: string;
  retryingKey: string | null;
  retry: Retry;
  queryClient: QueryClient;
}

export function extractionCandidate(
  job: ExtractionJobResponse,
  context: AdapterContext,
): ProcessingCandidate {
  const { intl, scopeId: homeId, retryingKey, retry, queryClient } = context;
  const key = `document:${job.id}`;
  const state = extractionState(job.status);
  const kind = job.classification ? classificationLabel(intl, job.classification) : null;
  const title = jobLabel(intl, job);
  const retryAction = () => retry(key, async () => {
    await retryExtractionJob(homeId, job.id);
    await queryClient.invalidateQueries({ queryKey: extractionJobKeys.all(homeId) });
  });

  return {
    key,
    resourceType: 'document',
    resourceId: job.id,
    title,
    notificationTitle: title,
    detail: extractionDetail(job, kind),
    state,
    startedAt: job.started_at ?? job.created_at,
    trackWhenNew: true,
    primaryAction: job.status === 'ready_for_review'
      ? linkAction(intl, 'review', route('homeActivityReview', { homeId, itemId: job.id }), title)
      : job.status === 'failed'
        ? retryActionModel(intl, title, retryingKey === key, retryAction)
        : job.status === 'completed' && job.target_id
          ? linkAction(intl, 'open', jobTargetRoute(homeId, job), title)
          : undefined,
  };
}

export function listingCandidate(
  listing: PropertyListingResponse,
  context: AdapterContext,
): ProcessingCandidate {
  const { intl, scopeId: groupId, retryingKey, retry, queryClient } = context;
  const key = `listing:${listing.id}`;
  const title = listing.title?.trim() || hostname(listing.url) || intl.formatMessage({
    id: '2e45636c-03cc-4444-9788-57f5d0645091',
    defaultMessage: 'Saved listing',
  });
  const state = enrichmentState(listing.enrichment_status ?? 'idle');
  const canRetry = state === 'failed' && !!listing.url;

  return {
    key,
    resourceType: 'listing',
    resourceId: listing.id,
    title,
    notificationTitle: title,
    detail: enrichmentDetail('listing', state),
    state,
    startedAt: listing.enrichment_started_at,
    primaryAction: canRetry
      ? retryActionModel(intl, title, retryingKey === key, () => retry(key, async () => {
          await enrichListing(listing.id, { url: listing.url! });
          await queryClient.invalidateQueries({ queryKey: listingKeys.all(groupId) });
        }))
      : state === 'succeeded' || state === 'failed'
        ? linkAction(
            intl,
            'open',
            route('groupListing', { groupId, listingId: listing.id }),
            title,
          )
        : undefined,
  };
}

export function assetCandidate(
  asset: AssetSummaryResponse,
  context: AdapterContext,
): ProcessingCandidate {
  const { intl, scopeId: homeId, retryingKey, retry, queryClient } = context;
  const key = `asset:${asset.id}`;
  const title = asset.name?.trim() || intl.formatMessage({
    id: '78c421be-a5ae-4a58-819f-ce8c8f08d77c',
    defaultMessage: 'Inventory item',
  });
  const state = enrichmentState(asset.enrichment_status ?? 'idle');
  const canRetry = state === 'failed' && !!asset.source_url;

  return {
    key,
    resourceType: 'asset',
    resourceId: asset.id,
    title,
    notificationTitle: title,
    detail: enrichmentDetail('asset', state),
    state,
    startedAt: asset.enrichment_started_at,
    primaryAction: canRetry
      ? retryActionModel(intl, title, retryingKey === key, () => retry(key, async () => {
          await enrichAsset(asset.id, asset.source_url!);
          await queryClient.invalidateQueries({ queryKey: assetKeys.all(homeId) });
        }))
      : state === 'succeeded' || state === 'failed'
        ? linkAction(intl, 'open', route('homeAsset', { homeId, assetId: asset.id }), title)
        : undefined,
  };
}

function extractionState(status: ExtractionJobStatus): ProcessingState {
  if (status === 'queued') return 'queued';
  if (status === 'processing') return 'running';
  if (status === 'ready_for_review') return 'needs_action';
  if (status === 'failed') return 'failed';
  return 'succeeded';
}

function enrichmentState(status: 'idle' | 'pending' | 'processing' | 'failed'): ProcessingState {
  if (status === 'pending') return 'queued';
  if (status === 'processing') return 'running';
  if (status === 'failed') return 'failed';
  return 'succeeded';
}

function extractionDetail(job: ExtractionJobResponse, kind: string | null) {
  if (job.status === 'queued') return <FormattedMessage id="d96c5e19-d716-471a-b56c-ac76f7975d28" defaultMessage="Waiting to start…" />;
  if (job.status === 'processing') return <FormattedMessage id="a4c44fb9-9471-4a43-9de6-bb2cbd28836c" defaultMessage="Reading the file…" />;
  if (job.status === 'ready_for_review') return kind
    ? <FormattedMessage id="47279fc5-250d-43f0-9626-300431c81127" defaultMessage="Ready · suggested {kind}" values={{ kind }} />
    : <FormattedMessage id="70c11c29-c903-40b2-aed1-676a459dbe4b" defaultMessage="Ready for your review" />;
  if (job.status === 'failed') return <ProcessingErrorMessage code={job.error_code} />;
  return kind
    ? <FormattedMessage id="552892e9-a8dc-4456-b51b-bae2fa3f30db" defaultMessage="Filed as {kind}" values={{ kind }} />
    : <FormattedMessage id="ffacbf8d-60d4-422c-b266-5f96664d4219" defaultMessage="Processed" />;
}

function enrichmentDetail(kind: 'listing' | 'asset', state: ProcessingState) {
  if (state === 'queued') return <FormattedMessage id="81b45745-dbac-46bf-923b-e542e1a346fc" defaultMessage="Waiting to enrich…" />;
  if (state === 'running') return kind === 'listing'
    ? <FormattedMessage id="6baded2f-30be-4299-8692-f990bccb25a0" defaultMessage="Enriching listing…" />
    : <FormattedMessage id="9df78372-12bc-4fa9-ab73-5e5dce357b69" defaultMessage="Reading product details…" />;
  if (state === 'failed') return kind === 'listing'
    ? <FormattedMessage id="c0e43a24-ad71-4726-bbe5-f99d7a1d35a1" defaultMessage="Listing enrichment failed" />
    : <FormattedMessage id="2674be3b-197e-4f62-9877-616b88f6a76f" defaultMessage="Product enrichment failed" />;
  return kind === 'listing'
    ? <FormattedMessage id="6883325f-8324-46a2-bc6a-e4fabbe82720" defaultMessage="Listing ready" />
    : <FormattedMessage id="23b3fa44-0b15-4231-ac9d-eb807622a68b" defaultMessage="Product details ready" />;
}

function linkAction(
  intl: IntlShape,
  kind: 'open' | 'review',
  href: string,
  title: string,
): ProcessingAction {
  const label = kind === 'review'
    ? intl.formatMessage({ id: '7034cc23-ed63-468d-a1c6-b59b93f2c71f', defaultMessage: 'Review' })
    : intl.formatMessage({ id: '098e863e-0e08-4ff6-973e-f5870f3025fe', defaultMessage: 'Open' });
  return {
    label,
    ariaLabel: intl.formatMessage(
      kind === 'review'
        ? { id: 'e8623d72-929f-43f6-ad23-de55e5b0e32b', defaultMessage: 'Review {title}' }
        : { id: '6766785a-b61e-46c0-9b29-273ae61e30b6', defaultMessage: 'Open {title}' },
      { title },
    ),
    href,
  };
}

function retryActionModel(
  intl: IntlShape,
  title: string,
  loading: boolean,
  onInvoke: () => void,
): ProcessingAction {
  return {
    label: intl.formatMessage({ id: '38dad2bc-25f3-4562-a48d-ad03d84785cc', defaultMessage: 'Try again' }),
    ariaLabel: intl.formatMessage(
      { id: '2d50cfd7-e166-43fd-8692-a8127b738ff7', defaultMessage: 'Try processing {title} again' },
      { title },
    ),
    loading,
    onInvoke,
  };
}

function hostname(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
